import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  FileSignature,
  ClipboardList,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Calendar,
  ArrowLeft,
  Folder,
  Bell,
  Activity,
  Edit2,
  ChevronDown,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import { useOrg } from "../../contexts/OrgContext";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import Button from "../../components/common/ui/button/Button";
import Badge from "../../components/common/ui/badge/Badge";
import { Card } from "../../components/common/Card";
import Tabs, { Tab } from "../../components/common/ui/tabs/Tabs";
import {
  Request,
  RequestStatus,
  RequestType,
  RequestParticipant,
  ParticipantStatus,
  FolderResponseDto,
  EnvelopeStatus,
} from "@secure-caseworker/shared";
import type { AuditLogEntry, AuditLogFilters } from "../../types/audit";
import {
  AddParticipantModal,
  AddParticipantParams,
} from "../../components/Requests/AddParticipantModal";
import {
  EditRemindersModal,
  EditRemindersParams,
} from "../../components/Requests/EditRemindersModal";
import {
  ParticipantsTab,
  RemindersTab,
  ActivityTab,
} from "../../components/Requests/tabs";
import { useToast } from "../../contexts/ToastContext.tsx";

export const RequestDetailPage: React.FC = () => {
  const { caseId, requestId } = useParams<{
    caseId: string;
    requestId: string;
  }>();
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const { navigateOrg, navigateOrgUrl, orgId } = useOrg();
  const [request, setRequest] = useState<Request | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("participants");
  const [activityLogs, setActivityLogs] = useState<AuditLogEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Edit states
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [dueDate, setDueDate] = useState<string>("");

  // Output folder edit states
  const [editingFolder, setEditingFolder] = useState(false);
  const [folders, setFolders] = useState<FolderResponseDto[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState<string>("");

  // Add participant modal state
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);

  // Edit reminders modal state
  const [showEditRemindersModal, setShowEditRemindersModal] = useState(false);
  const [editRemindersMode, setEditRemindersMode] = useState<"single" | "all">(
    "single",
  );
  const [editRemindersParticipant, setEditRemindersParticipant] =
    useState<RequestParticipant | null>(null);

  useEffect(() => {
    if (requestId) {
      loadRequest();
    }
  }, [requestId]);

  useEffect(() => {
    if (activeTab === "activity" && requestId && orgId) {
      loadActivityLogs();
    }
  }, [activeTab, requestId, orgId]);

  const loadRequest = async () => {
    if (!requestId) return;

    setLoading(true);
    try {
      const data = await apiClient.getRequest(requestId);
      setRequest(data);
      setDueDate(data.due_date || "");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load request",
      });
      navigateOrg(`/cases/${caseId}`);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    if (!requestId || !orgId) return;

    setActivityLoading(true);
    try {
      const filters: AuditLogFilters = {
        organizationId: orgId,
        resourceType: "request",
        resourceId: requestId,
        limit: 50,
      };
      const response = await apiClient.getAuditLogs(filters);
      setActivityLogs(response.logs);
    } catch (error) {
      console.error("Failed to load activity logs:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const loadFolders = async () => {
    if (!caseId) return;

    setFoldersLoading(true);
    try {
      const data = await apiClient.getAllFolders(caseId);
      setFolders(data);
    } catch (error) {
      console.error("Failed to load folders:", error);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleStartEditingFolder = () => {
    loadFolders();
    setSelectedFolderId(request?.output_folder_id || "");
    setEditingFolder(true);
    setShowNewFolderInput(false);
    setNewFolderName("");
  };

  const handleCancelEditingFolder = () => {
    setEditingFolder(false);
    setShowNewFolderInput(false);
    setNewFolderName("");
    setSelectedFolderId("");
  };

  const handleUpdateOutputFolder = async () => {
    if (!requestId || !caseId) return;

    try {
      let folderId: string | null = selectedFolderId || null;

      // If creating a new folder
      if (showNewFolderInput && newFolderName.trim()) {
        const newFolder = await apiClient.createFolder({
          name: newFolderName.trim(),
          caseId: caseId,
        });
        folderId = newFolder.id;
      }

      const updatedRequest = await apiClient.updateRequest(requestId, {
        output_folder_id: folderId,
      });
      setRequest(updatedRequest);
      showToast("Output folder updated successfully");
    } catch (error) {
      showToast("Failed to update output folder", "error");
    } finally {
      handleCancelEditingFolder();
    }
  };

  const handleCancelRequest = async () => {
    if (!requestId) return;
    if (!confirm("Are you sure you want to cancel this request?")) return;

    try {
      await apiClient.cancelRequest(requestId);
      showToast("Request canceled successfully");

      loadRequest();
    } catch (error) {
      showToast("Failed to cancel request", "error");
    }
  };

  const handleResendRequest = async (participantId: string) => {
    if (!requestId) {
      return;
    }
    try {
      await apiClient.resendRequest(requestId, [participantId]);
      showToast("Request resent successfully");
    } catch (error) {
      showToast("Failed to resend request", "error");
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!confirm("Are you sure you want to remove this participant?")) return;

    try {
      await apiClient.removeParticipant(participantId);
      showToast("Participant removed successfully");

      loadRequest();
    } catch (error) {
      showToast("Failed to remove participant", "error");
    }
  };

  const handleUpdateDueDate = async () => {
    if (!requestId) return;

    try {
      const updatedRequest = await apiClient.updateRequest(requestId, {
        due_date: dueDate || null,
      });
      setRequest(updatedRequest);
      showToast("Due date updated successfully");
    } catch (error) {
      showToast("Failed to update due date", "error");
    } finally {
      setEditingDueDate(false);
    }
  };

  const handleAddParticipant = async (params: AddParticipantParams) => {
    if (!requestId) return;

    // Add the participant
    const newParticipant = await apiClient.addParticipantToRequest(requestId, [
      params.participant,
    ]);

    // If reminders are specified and request has a due date, schedule them
    if (params.reminders.length > 0 && request?.due_date) {
      const participantId = Array.isArray(newParticipant)
        ? newParticipant[0]?.id
        : newParticipant?.id;
      if (participantId) {
        await apiClient.scheduleParticipantReminders(
          participantId,
          request.due_date,
          params.reminders,
        );
      }
    }

    showToast("An invitation email has been sent to the participant.");

    // Reload request to get updated participants
    loadRequest();
  };

  const handleEditReminders = (participant: RequestParticipant) => {
    setEditRemindersParticipant(participant);
    setEditRemindersMode("single");
    setShowEditRemindersModal(true);
  };

  const handleEditAllReminders = () => {
    setEditRemindersParticipant(null);
    setEditRemindersMode("all");
    setShowEditRemindersModal(true);
  };

  const handleSaveReminders = async (params: EditRemindersParams) => {
    if (!request?.due_date) return;

    if (editRemindersMode === "single" && editRemindersParticipant) {
      // Update reminders for single participant
      await apiClient.scheduleParticipantReminders(
        editRemindersParticipant.id,
        request.due_date,
        params.reminders,
      );
      showToast(
        `Reminder schedule updated for ${
          editRemindersParticipant.name || editRemindersParticipant.email
        }.`,
      );
    } else if (editRemindersMode === "all") {
      // Update reminders for all pending participants
      const pendingParticipants =
        request.request_participants?.filter(
          (p) => p.status === ParticipantStatus.PENDING,
        ) || [];

      await Promise.all(
        pendingParticipants.map((p) =>
          apiClient.scheduleParticipantReminders(
            p.id,
            request.due_date!,
            params.reminders,
          ),
        ),
      );

      showToast(
        `Reminder schedule updated for ${
          pendingParticipants.length
        } participant${pendingParticipants.length !== 1 ? "s" : ""}.`,
      );
    }

    // Reload to reflect changes
    loadRequest();
  };

  const getStatusBadge = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.PENDING:
        return (
          <Badge color="warning">
            <Clock size={14} className="mr-1" /> Pending
          </Badge>
        );
      case RequestStatus.SENT:
        return (
          <Badge color="info">
            <Send size={14} className="mr-1" /> Sent
          </Badge>
        );
      case RequestStatus.IN_PROGRESS:
        return (
          <Badge color="info">
            <AlertCircle size={14} className="mr-1" /> In Progress
          </Badge>
        );
      case RequestStatus.COMPLETED:
        return (
          <Badge color="success">
            <CheckCircle size={14} className="mr-1" /> Completed
          </Badge>
        );
      case RequestStatus.CANCELED:
        return (
          <Badge color="error">
            <XCircle size={14} className="mr-1" /> Canceled
          </Badge>
        );
      default:
        return <Badge color="light">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: RequestType) => {
    const styles =
      type === RequestType.SIGNATURE
        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
    const Icon = type === RequestType.SIGNATURE ? FileSignature : ClipboardList;
    const label = type === RequestType.SIGNATURE ? "Signature" : "Form";
    return (
      <span
        className={`inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full font-medium ${styles}`}
      >
        <Icon size={14} />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading request...
          </p>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const canEdit =
    request.status !== RequestStatus.COMPLETED &&
    request.status !== RequestStatus.CANCELED;

  const tabs: Tab[] = [
    {
      id: "participants",
      label: "Participants",
      icon: <Users size={16} />,
    },
    {
      id: "reminders",
      label: "Reminders",
      icon: <Bell size={16} />,
    },
    {
      id: "activity",
      label: "Activity",
      icon: <Activity size={16} />,
    },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: "Cases", path: navigateOrgUrl("/cases") },
          { label: "Case Details", path: navigateOrgUrl(`/cases/${caseId}`) },
          { label: "Request Details" },
        ]}
      />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateOrg(`/cases/${caseId}`)}
            className="p-2 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            {request.template?.type === RequestType.SIGNATURE ? (
              <FileSignature size={32} className="text-purple-500" />
            ) : (
              <ClipboardList size={32} className="text-brand-500" />
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-title-md2 font-semibold text-black dark:text-white">
                  {request.template?.title || "Request"}
                </h1>
                {getTypeBadge(request.template?.type || RequestType.FORM)}
                {getStatusBadge(request.status)}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {request.template?.description || "No description"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {canEdit && (
            <Button variant="danger" onClick={handleCancelRequest}>
              <XCircle size={18} className="mr-2" />
              Cancel Request
            </Button>
          )}
        </div>
      </div>

      {/* Envelope Failed Warning */}
      {request.envelopes?.some(
        (e: { envelope_status: EnvelopeStatus }) =>
          e.envelope_status === EnvelopeStatus.FAILED,
      ) &&
        (() => {
          const failedEnvelope = request.envelopes?.find(
            (e: { envelope_status: EnvelopeStatus }) =>
              e.envelope_status === EnvelopeStatus.FAILED,
          );
          const documents = failedEnvelope?.metadata
            ?.deliverable_documents as string[] | undefined;
          return (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-500 mt-0.5" size={20} />
                <div>
                  <h3 className="font-semibold text-red-700 dark:text-red-400">
                    Signature Request Failed
                  </h3>
                  <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                    The signed document could not be generated. Please cancel
                    this request and create a new one to retry.
                  </p>
                  {documents && documents.length > 0 && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                      Affected documents: {documents.join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Due Date Card */}
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <Calendar
                  size={20}
                  className="text-gray-600 dark:text-gray-400"
                />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Due Date
                </p>
                {editingDueDate ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="text-sm dark:text-white border rounded px-2 py-1 dark:bg-gray-800 dark:border-gray-700"
                    />
                    <button
                      onClick={handleUpdateDueDate}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      onClick={() => setEditingDueDate(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                ) : (
                  <p className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
                    {request.due_date
                      ? new Date(request.due_date).toLocaleDateString()
                      : "No due date"}
                    {canEdit && (
                      <button
                        onClick={() => setEditingDueDate(true)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Output Folder Card */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <Folder
                  size={20}
                  className="text-gray-600 dark:text-gray-400"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Output Folder
                </p>
                {editingFolder ? (
                  <div className="mt-1 space-y-2">
                    {showNewFolderInput ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="New folder name"
                          className="flex-1 text-sm dark:text-white border rounded px-2 py-1 dark:bg-gray-800 dark:border-gray-700"
                          autoFocus
                        />
                        <button
                          onClick={() => setShowNewFolderInput(false)}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <select
                            value={selectedFolderId}
                            onChange={(e) =>
                              setSelectedFolderId(e.target.value)
                            }
                            disabled={foldersLoading}
                            className="w-full text-sm dark:text-white border rounded pl-2 pr-7 py-1 dark:bg-gray-800 dark:border-gray-700 appearance-none"
                          >
                            <option value="">
                              {foldersLoading ? "Loading..." : "Default"}
                            </option>
                            {folders.map((folder) => (
                              <option key={folder.id} value={folder.id}>
                                {folder.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={14}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                          />
                        </div>
                        <button
                          onClick={() => setShowNewFolderInput(true)}
                          className="text-xs text-brand-600 hover:text-brand-700 whitespace-nowrap"
                        >
                          + New
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleUpdateOutputFolder}
                        className="text-brand-600 hover:text-brand-700"
                        disabled={showNewFolderInput && !newFolderName.trim()}
                      >
                        <CheckCircle size={16} />
                      </button>
                      <button
                        onClick={handleCancelEditingFolder}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
                    {request.output_folder_id
                      ? (request.output_folder?.name ?? "Configured")
                      : "Default"}
                    {canEdit && (
                      <button
                        onClick={handleStartEditingFolder}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Participants Summary Card */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Users size={20} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Participants
              </p>
              <p className="text-lg font-semibold text-black dark:text-white">
                {request.request_participants?.filter(
                  (p) => p.status === ParticipantStatus.COMPLETED,
                ).length || 0}
                {" / "}
                {request.request_participants?.length || 0} completed
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs Section */}
      <Card className="p-0 overflow-hidden">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Participants Tab */}
        {activeTab === "participants" && (
          <ParticipantsTab
            participants={request.request_participants || []}
            canEdit={canEdit}
            onResendInvitation={handleResendRequest}
            onEditReminders={handleEditReminders}
            onRemoveParticipant={handleRemoveParticipant}
            onAddParticipant={() => setShowAddParticipantModal(true)}
          />
        )}

        {/* Reminders Tab */}
        {activeTab === "reminders" && (
          <RemindersTab
            participants={request.request_participants || []}
            canEdit={canEdit}
            hasDueDate={!!request.due_date}
            onEditReminders={handleEditReminders}
            onEditAllReminders={handleEditAllReminders}
          />
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && (
          <ActivityTab logs={activityLogs} loading={activityLoading} />
        )}
      </Card>

      {/* Request Details */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
          <FileText size={20} />
          Request Details
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Request ID</p>
            <p className="font-mono text-black dark:text-white text-xs">
              {request.id}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Template ID</p>
            <p className="font-mono text-black dark:text-white text-xs">
              {request.template_id}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Created</p>
            <p className="text-black dark:text-white">
              {new Date(request.created_at).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Last Updated</p>
            <p className="text-black dark:text-white">
              {new Date(request.updated_at).toLocaleString()}
            </p>
          </div>
        </div>
      </Card>

      {/* Add Participant Modal */}
      <AddParticipantModal
        isOpen={showAddParticipantModal}
        onClose={() => setShowAddParticipantModal(false)}
        onAdd={handleAddParticipant}
        hasDueDate={!!request.due_date}
      />

      {/* Edit Reminders Modal */}
      <EditRemindersModal
        isOpen={showEditRemindersModal}
        onClose={() => {
          setShowEditRemindersModal(false);
          setEditRemindersParticipant(null);
        }}
        onSave={handleSaveReminders}
        mode={editRemindersMode}
        participantName={
          editRemindersParticipant?.name || editRemindersParticipant?.email
        }
        participantCount={
          request.request_participants?.filter(
            (p) => p.status === ParticipantStatus.PENDING,
          ).length || 0
        }
        currentReminders={
          editRemindersParticipant?.participant_reminders
            ?.filter((r) => r.status === "pending")
            .map((r) => ({ timing: r.timing, offset_days: r.offset_days })) ||
          []
        }
        hasDueDate={!!request.due_date}
      />
    </div>
  );
};
